import type { EngineErrorCode } from './types';

/**
 * Error with a stable, translatable code. Workers serialize errors, so the code
 * travels in the message prefix and is restored by {@link toEngineError}.
 */
export class EngineError extends Error {
  constructor(
    readonly code: EngineErrorCode,
    message: string = code,
    readonly detail?: string,
  ) {
    super(`[${code}] ${message}`);
    this.name = 'EngineError';
  }
}

const CODE_PATTERN = /\[([a-z-]+)\]\s?(.*)/s;
const KNOWN_CODES: readonly EngineErrorCode[] = [
  'unsupported-format',
  'no-video',
  'no-audio',
  'codec-unsupported',
  'browser-unsupported',
  'no-encoder',
  'empty-output',
  'size-limit-too-small',
  'storage',
  'download-failed',
  'frames-unreadable',
  'source-unreadable',
  'stalled',
  'unknown',
];

/** Restores an {@link EngineError} from anything thrown across a worker boundary. */
export function toEngineError(error: unknown): EngineError {
  if (error instanceof EngineError) return error;
  const message = error instanceof Error ? error.message : String(error);
  const match = CODE_PATTERN.exec(message);
  const code = match?.[1] as EngineErrorCode | undefined;
  if (code && KNOWN_CODES.includes(code)) return new EngineError(code, match![2] || code, match![2]);
  // Workers send errors back as plain objects, so a DOMException arrives as an Error with its name.
  if (error instanceof Error && error.name === 'QuotaExceededError') return new EngineError('storage', message);
  return new EngineError('unknown', message, message);
}
